/**
 * Formatadores do cartão de bloco da linha aberta de Meus Estudos (Flux Personalizado, #2002,
 * .dc.html:334-342). As datas são `DateOnly` e saem pelo `formatDateOnly` (#1991), sem `Date`:
 * nenhum dia desloca com o fuso de quem abre a tela.
 */

import { formatDateOnly } from 'helper/flux/studyDates';
import { ControlPanelOption } from 'helper/products/meteorology';
import { DateOnly } from 'types/Flux/Personalized/myStudies';

/** Valor que falta no cartão: as posições da grade de 2 colunas não mudam. */
export const MISSING_VALUE = '—';

/** Título de reserva de um cenário sem nome (o nome é o rótulo do grupo dos blocos). */
export const UNNAMED_SCENARIO = 'Cenário sem nome';

const LAST_RUN_HOUR = 23;

/**
 * Rótulo da rodada no cabeçalho do bloco (Q7). O adapter já descarta rodada fora de 0 a 23; aqui
 * é só defesa, e um valor fora disso omite o rótulo em vez de mostrar "Rodada 24h UTC".
 *
 * @param runTime - Hora da rodada em UTC (0 a 23; 0 e 12 na prática), ou null sem rodada.
 * @returns 'Rodada 00h UTC', 'Rodada 12h UTC'...; null quando o rótulo é omitido.
 */
export function formatRunTime(runTime: number | null): string | null {
  if (runTime === null || !Number.isInteger(runTime) || runTime < 0 || runTime > LAST_RUN_HOUR) {
    return null;
  }
  return `Rodada ${String(runTime).padStart(2, '0')}h UTC`;
}

/**
 * Modelo do bloco em caixa-alta, como o bloco do Upload (`Row/Scenario/Block`).
 *
 * @param model - Modelo do bloco (o adapter o põe em minúsculas).
 * @returns O modelo em caixa-alta, ou '—' quando vem vazio.
 */
export function formatModel(model: string): string {
  const trimmed = model.trim();
  return trimmed === '' ? MISSING_VALUE : trimmed.toUpperCase();
}

/**
 * Título do cenário no painel.
 *
 * @param name - Nome do cenário.
 * @returns O nome sem os espaços das pontas, ou 'Cenário sem nome' quando vem vazio.
 */
export function formatScenarioName(name: string): string {
  return name.trim() || UNNAMED_SCENARIO;
}

/**
 * Viés do bloco (`ck_rmv`), como no protótipo.
 *
 * @param biasRemoval - Com remoção de viés.
 * @returns 'COM remoção' ou 'SEM remoção'.
 */
export function formatBiasRemoval(biasRemoval: boolean): string {
  return biasRemoval ? 'COM remoção' : 'SEM remoção';
}

/**
 * Recorte como intervalo de datas (D11): o `recorte_regiao` fica de fora.
 *
 * Fim antes do início sai como veio, sem reordenar: o cartão mostra o que o backend gravou, e
 * inverter as pontas esconderia o dado inconsistente de quem o configurou.
 *
 * @param start - Início do recorte, ou null.
 * @param end - Fim do recorte, ou null.
 * @returns 'dd/mm/aaaa – dd/mm/aaaa'; '—' sem recorte; a ponta que falta vira '—'.
 */
export function formatCropRange(start: DateOnly | null, end: DateOnly | null): string {
  if (start === null && end === null) return MISSING_VALUE;
  const from = start === null ? MISSING_VALUE : formatDateOnly(start);
  const to = end === null ? MISSING_VALUE : formatDateOnly(end);
  return `${from} – ${to}`;
}

/**
 * Rótulo do membro pelas opções do painel do Flux (`painel-options`), como o bloco do Upload
 * (`Row/Scenario/Block`): o membro do modelo do bloco, ou o próprio id quando as opções ainda
 * não chegaram, falharam ou não conhecem o modelo ou o membro.
 *
 * @param options - Opções do painel do Flux, ou undefined sem resposta.
 * @param model - Modelo do bloco, em minúsculas (como o `modelo_value` do painel).
 * @param member - Id do membro, ou null.
 * @returns O rótulo, o id do membro, ou null quando o bloco não tem membro.
 */
export function resolveMemberLabel(
  options: readonly ControlPanelOption[] | undefined,
  model: string,
  member: string | null,
): string | null {
  if (member === null) return null;
  const modelOptions = options?.find((option) => option.model.value === model);
  return modelOptions?.members.find((candidate) => candidate.id === member)?.label || member;
}
